import pclnExperimentation from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import { SegmentedChipGroup } from '@pcln/horizon'
import type { HotelFormState, LocationSearchType, MultiHotelFormState } from '@pcln/horizon-search-form'
import { HotelSearchForm, MultiHotelSearchForm } from '@pcln/horizon-search-form'
import { useCallback, useContext, useMemo, useRef, useState, type SetStateAction } from 'react'
import Sticky from 'react-stickynode'

import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import StickyEffect from '@/components/StickyEffect'
import fireMaxRoomsEvent from '@/components/TravelerSelection/ga4'
import type { TypeAheadConfigEndpoint } from '@/components/TypeAhead/types'
import { SearchFormContext } from '@/context/SearchFormContext'
import type { GA4PageNameType } from '@/ga4types'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import { fireBundleAndSaveEvent, fireMultiHotelRadioClick } from '../ga4'
import { handleSubmitForm as handleMultiHotelSubmitForm } from '../SearchForm/MultiHotelSearchForm/utils'
import { groupDealsURL } from '../SearchForm/utils'
import type {
  HOTEL_FORM_STATE_TYPE,
  HOTEL_FORM_SUBMIT_TYPE,
  SelectedTopFilters,
  SEM_SEARCH_TYPE,
} from '../types'
import { resolveAiQuery } from './aiSearch'
import { AI_SEARCH_MODE, SEARCH_MODE_OPTIONS, STANDARD_SEARCH_MODE } from './aiSearchConstants'
import { AiSearchForm, type AiSearchSubmitParams } from './AiSearchForm'
import FormTypeRadios from './HorizonFormTypeRadio'
import HorizonSelectedFilterChipBar from './HorizonSelectedFilterChipBar'
import {
  logAiSearchModeToggle,
  logAiSearchNoRecommendationsSwitch,
  logAiSearchResolveFailed,
} from './logAiSearch'
import useHotelFormInitialValues from './useHotelFormInitialValues'

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) => void
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  showHotelTypeButtons?: boolean
  pageName?: 'homepage' | 'landing'
}

export default function HorizonHotelForm({
  onSubmit,
  setLocation = () => null,
  showHotelTypeButtons = false,
  pageName = 'homepage',
}: FormType) {
  const { initialValues, isMobile, cguid, SEMSearch, SEMSEARCH } = useHotelFormInitialValues()
  const { updateFormValues } = useContext(SearchFormContext)
  const useV2Typeahead = useFeature('siteExperience.useV2Typeahead') ?? false
  const typeAheadEndpointHotels = useV2Typeahead ? 'hotelsV2' : 'hotels'

  const [hotelType, setHotelType] = useState('single')
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const [hotelTripType, setHotelTripType] = useState('STAY')
  const [searchMode, setSearchMode] = useState<string>(STANDARD_SEARCH_MODE)
  const isHomepage = pageName === 'homepage'

  const aiSearchModeVariant = useSeti({
    experimentCode: 'AI_SEARCH_IN_YOUR_OWN_WORDS',
    shouldFireImpression: isHomepage,
  })
  const showSearchModeChips = isHomepage && aiSearchModeVariant === 'VARIANT'
  const isAiSearchMode = showSearchModeChips && searchMode === AI_SEARCH_MODE

  const enableStickyEffect = !isMobile && pageName === 'homepage' && hotelType === 'single' && !isAiSearchMode

  const isAiSearchEnabled = isMobile && hotelType === 'single' && hotelTripType === 'STAY'

  const aiSearchVariant = useSeti({
    experimentCode: 'USB_SEARCH_AI_QUERIES',
    shouldFireImpression: isAiSearchEnabled,
  })

  const isLTRModelEnabled = !isMobile && hotelTripType === 'STAY'

  const ltrModelVariant = useSeti({
    experimentCode: 'USB_SEARCH_LTR_MODEL',
    shouldFireImpression: isLTRModelEnabled,
  })

  const hasLTRSearch = ltrModelVariant === 'VARIANT' && isLTRModelEnabled

  const hasAISearch =
    (aiSearchVariant === 'VARIANT_1' || aiSearchVariant === 'VARIANT_2') && isAiSearchEnabled

  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      if (enableStickyEffect) {
        setIsStickyInEffect(true)
      }
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }

  const isPartnersURL =
    useSeti({
      experimentCode: 'HP_LP_HOTELS_TEN_ROOMS',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const removeBackground =
    useSeti({ experimentCode: 'HP_SEARCH_FORM_HORIZON_CROSS_SELL_DESKTOP' }) === 'VARIANT'

  const aiSearchButtonVariant = useSeti({
    experimentCode: 'USB_AI_SEARCH_BUTTON_HP',
    shouldFireImpression: isHomepage,
  })
  const showAiSearchButton =
    isHomepage && (aiSearchButtonVariant === 'VARIANT1' || aiSearchButtonVariant === 'VARIANT2')

  const recentSearchImprovementVariant = useSeti({
    experimentCode: 'HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV',
    shouldFireImpression: false,
  })
  const mergeRecentAndRecommendedSearches = recentSearchImprovementVariant === 'VARIANT1'
  const preferRecentSearchesOnly = recentSearchImprovementVariant === 'VARIANT2'
  const fireRecentSearchImprovementImpression = useCallback(() => {
    pclnExperimentation.fireImpression('HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV')
  }, [])

  // Captures the typeahead `select_content` item_name for the selected destination
  // so the submit `search` event can carry the same value.
  const searchItemNameRef = useRef<string | null>(null)

  const [trackedValues, setTrackedValues] = useState<Partial<HotelFormState>>({
    endLocation: initialValues.endLocation as LocationSearchType | null,
    startDate: initialValues.startDate,
    endDate: initialValues.endDate,
    travelers: initialValues.travelers,
  })

  const hotelPlannerLink = groupDealsURL({
    location: trackedValues.endLocation?.itemName ?? '',
    startDate: trackedValues.startDate ?? '',
    endDate: trackedValues.endDate ?? '',
    isPartnersURL,
  })

  const multiHotelInitialData = useMemo(
    () => ({
      endLocation: trackedValues.endLocation ?? null,
      startDate: trackedValues.startDate ?? '',
      endDate: trackedValues.endDate ?? '',
      travelers: trackedValues.travelers ?? initialValues.travelers,
    }),
    [
      trackedValues.endLocation,
      trackedValues.startDate,
      trackedValues.endDate,
      trackedValues.travelers,
      initialValues.travelers,
    ],
  )

  const handleFormValuesChange = useCallback(
    (values: HotelFormState) => {
      setTrackedValues(values)
      updateFormValues?.(values as unknown as HOTEL_FORM_STATE_TYPE)
    },
    [updateFormValues],
  )

  const handleSubmit = useCallback(
    (data: HotelFormState) => {
      if (isMobile && typeof window.PCLN?.pclnEventDispatcher?.dispatch === 'function') {
        if (isHomepage) {
          window.PCLN.pclnEventDispatcher.dispatch('FIND YOUR HOTEL')
        }
        window.PCLN.pclnEventDispatcher.dispatch('FIND_YOUR_HOTEL_DATA', {
          cityName: data.endLocation?.cityName,
          cityId: data.endLocation?.cityID,
          checkInDate: data.startDate,
          checkOutDate: data.endDate,
        })
      }

      let userLatitude: number | undefined
      let userLongitude: number | undefined
      if (data.endLocation?.type === 'GEO' && data.endLocation?.lat && data.endLocation?.lon) {
        userLatitude = data.endLocation.lat
        userLongitude = data.endLocation.lon
      }

      const formValues = {
        ...data,
        ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
        ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
        useCurrentLocation: data.endLocation?.type === 'GEO',
        ...(userLatitude && userLongitude && { userLatitude, userLongitude }),
        ...(searchItemNameRef.current ? { searchItemName: searchItemNameRef.current } : {}),
      }

      onSubmit(formValues as unknown as HOTEL_FORM_SUBMIT_TYPE, pageName)
      logSearchFormAnalytics({
        cguid,
        pageName,
        productType: 'hotels',
        isMobile,
      })
    },
    [isMobile, isHomepage, SEMSearch, SEMSEARCH, onSubmit, pageName, cguid],
  )

  const handleMultiHotelSubmit = useCallback(
    (data: MultiHotelFormState) => {
      handleMultiHotelSubmitForm(data)
      logSearchFormAnalytics({
        cguid,
        pageName,
        productType: 'multi-hotel',
        isMobile,
      })
    },
    [cguid, isMobile, pageName],
  )

  const handleAiSearchSubmit = useCallback(
    async ({ query, startDate, endDate, travelers, resolvedLocation }: AiSearchSubmitParams) => {
      const endLocation = resolvedLocation ?? (await resolveAiQuery({ query }))
      if (!endLocation) {
        logAiSearchResolveFailed(query)
        throw new Error('AI_SEARCH_NO_MATCH')
      }

      // Delegate to the standard submit path (SEM params, GEO lat/lon, onSubmit, analytics)
      // instead of re-deriving it — same URL-building logic as the standard hotel form.
      handleSubmit({
        ...initialValues,
        endLocation,
        startDate,
        endDate,
        travelers,
      } as unknown as HotelFormState)
    },
    [handleSubmit, initialValues],
  )

  const renderStandardForm = () => (
    <>
      {showHotelTypeButtons && (
        <FormTypeRadios
          formType={hotelType}
          onChange={(selectedFormName) => {
            if (hasAISearch && selectedFormName === 'multi') {
              setTrackedValues((prev) => ({ ...prev, endLocation: null }))
            }
            setHotelType(selectedFormName)
            fireMultiHotelRadioClick(pageName)
          }}
        />
      )}
      <StickyEffect
        enabled={enableStickyEffect}
        isStickyInEffect={isStickyInEffect}
        handleStickyEffectStateChange={handleStickyEffectStateChange}
        innerZ={isMobile ? 0 : 3}
      >
        {hotelType === 'multi' && showHotelTypeButtons ? (
          <MultiHotelSearchForm
            initialData={multiHotelInitialData}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            onSubmit={handleMultiHotelSubmit}
            renderAutocomplete={(props) => (
              <AutocompleteWrapper
                ref={props.inputRef}
                searchKey={props.searchKey}
                label={props.label}
                typeAheadEndpoint={props.typeAheadEndpoint as TypeAheadConfigEndpoint}
                errorMessage={props.errorMessage}
                initialSelectedItem={props.initialSelectedItem}
                onItemSelect={(item) => props.onItemSelect(item as LocationSearchType | null)}
                isMobile={isMobile}
                mergeRecentAndRecommendedSearches={mergeRecentAndRecommendedSearches}
                preferRecentSearchesOnly={preferRecentSearchesOnly}
                onRecentSearchImprovementImpression={fireRecentSearchImprovementImpression}
                enableHotelNeighbourhoodNesting
                hasLTRSearch={hasLTRSearch}
              />
            )}
          />
        ) : (
          <HotelSearchForm
            initialValues={initialValues as unknown as Partial<HotelFormState>}
            isMobile={isMobile}
            autoAdvance={!isMobile}
            typeAheadEndpoint={typeAheadEndpointHotels}
            setLocation={setLocation}
            isStickyInEffect={isStickyInEffect}
            hotelPlannerLinkHref={hotelPlannerLink}
            removeBackground={removeBackground}
            onMaxRoomsClick={() => {
              pclnExperimentation.fireImpression('HP_LP_HOTELS_TEN_ROOMS')
              window.open(hotelPlannerLink, '_blank')
              fireMaxRoomsEvent(pageName, 'hotel')
            }}
            onBundleChange={(tripType, checked) => {
              setHotelTripType(tripType)
              if (checked) {
                try {
                  fireBundleAndSaveEvent(tripType)
                } catch {
                  // GA4TripTypeMapper may throw on unexpected trip type combinations
                }
              }
            }}
            onFormValuesChange={handleFormValuesChange}
            onSubmit={handleSubmit}
            renderAutocomplete={(props) => (
              <AutocompleteWrapper
                ref={props.inputRef}
                searchKey={props.searchKey}
                label={props.label}
                typeAheadEndpoint={props.typeAheadEndpoint as TypeAheadConfigEndpoint}
                errorMessage={props.errorMessage}
                initialSelectedItem={props.initialSelectedItem}
                onItemSelect={(item) => props.onItemSelect(item as LocationSearchType | null)}
                onSelectItemName={
                  props.searchKey === 'endLocation'
                    ? (name) => {
                        searchItemNameRef.current = name
                      }
                    : undefined
                }
                isMobile={isMobile}
                showCurrentLocation={props.showCurrentLocation}
                onGeolocationError={props.onGeolocationError}
                selectedFilterChipData={
                  props.selectedFilterChipData as unknown as SelectedTopFilters | undefined
                }
                setSelectedFilterChipData={
                  props.setSelectedFilterChipData as unknown as
                    | ((value: SetStateAction<SelectedTopFilters>) => void)
                    | undefined
                }
                showFilterChips={props.showFilterChips}
                aiSearchButtonVariant={showAiSearchButton ? aiSearchButtonVariant : undefined}
                mergeRecentAndRecommendedSearches={mergeRecentAndRecommendedSearches}
                preferRecentSearchesOnly={preferRecentSearchesOnly}
                onRecentSearchImprovementImpression={fireRecentSearchImprovementImpression}
                enableHotelNeighbourhoodNesting={hotelTripType === 'STAY'}
                hasAISearch={hasAISearch}
                hasLTRSearch={hasLTRSearch}
              />
            )}
            renderSelectedFilterChipBar={(chipBarProps) => (
              <HorizonSelectedFilterChipBar
                selectedFilterChipData={chipBarProps.selectedFilterChipData as unknown as SelectedTopFilters}
                setSelectedFilterChipData={
                  chipBarProps.setSelectedFilterChipData as unknown as (
                    value: SetStateAction<SelectedTopFilters>,
                  ) => void
                }
              />
            )}
          />
        )}
      </StickyEffect>
    </>
  )

  if (showSearchModeChips) {
    // Mobile drops the leading icons so the label and Beta badge fit the full-width track.
    const searchModeOptions = isMobile
      ? SEARCH_MODE_OPTIONS.map((option) => ({ ...option, iconLeft: undefined, customIcon: undefined }))
      : SEARCH_MODE_OPTIONS

    return (
      <div className='flex w-auto flex-col gap-3'>
        <div className={isMobile ? 'w-full' : undefined}>
          <SegmentedChipGroup
            name='search-mode'
            options={searchModeOptions}
            value={searchMode}
            handleChange={(value: unknown) => {
              const nextMode = String(value)
              if (nextMode === searchMode) return
              logAiSearchModeToggle(nextMode)
              setSearchMode(nextMode)
            }}
            aria-label='Search mode'
            fullWidth={isMobile}
          />
        </div>
        {isAiSearchMode ? (
          <AiSearchForm
            isMobile={isMobile}
            initialStartDate={initialValues.startDate}
            initialEndDate={initialValues.endDate}
            initialTravelers={initialValues.travelers}
            onSubmitQuery={handleAiSearchSubmit}
            onSwitchToStandardSearch={(searchQuery) => {
              logAiSearchNoRecommendationsSwitch(searchQuery)
              setSearchMode(STANDARD_SEARCH_MODE)
            }}
          />
        ) : (
          renderStandardForm()
        )}
      </div>
    )
  }

  return <div className='flex w-auto flex-col gap-3'>{renderStandardForm()}</div>
}
